import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { Collapse, TableCell, TableRow } from "@material-ui/core/";

import classes from "./GenomeTable.module.css";

import GenomeTableCells from "./GenomeTableCells/GenomeTableCells";
import FileTable from "../FileTable/FileTable";
import { selectedForRow } from "../../utils/FileUtils";

import { useSearchParams } from "../../hooks/useSearchParams";
// import { base as ldbase } from "../../utils/LDFFUtils";
import * as cartTool from "../../utils/CartDataTools";

import { getLocalStorageHideSelectionOK } from "../../utils/LocalStorageUtils";

import { CONFIRM_APPLY_LOCAL_FILTER } from "../UI/InfoBoard/ConfirmChange";

import {
  ROW_SELECTED_NONE,
  ROW_SELECTED_SOME,
  ROW_SELECTED_ALL,
  ROW_ALL_IN_CART,
} from "./Constants";

const GenomeRow = ({
  row,
  selectedFiles,
  setSelectedFiles, // SearchPageContainer.setSelectedFiles
  expanded,
  onRowClick,
  handleGenomeLevelCheckboxClicked,
  ldClient,
  cartData,
  restoreid,
  setAcceptConfirmationHandler,
  setConfirmApplyType,
  honeycomb,
  ...props
}) => {
  const { cart: cartpage } = useSearchParams();
  const [selectState, setSelectState] = useState(ROW_SELECTED_NONE); // 0: none selected; 1: some selected, 2: all selected
  const [rowFilters, setRowFilters] = useState(() => new Set());
  const [filteredFiles, setFilteredFiles] = useState(row?.files);
  const [filteredFileSize, setFilteredFileSize] = useState(row?.fileSize);
  const [virtualDirectoryStructureAnchor, setVirtualDirectoryStructureAnchor] =
    useState(null);

  const updateRowFilters = (newFilters) => {
    if (newFilters)
      setRowFilters((prev) => {
        const newRowFilters = new Set(prev);
        if (newRowFilters.has(newFilters)) newRowFilters.delete(newFilters);
        else newRowFilters.add(newFilters);
        return newRowFilters;
      });
  };

  const resetRowFilters = () => {
    setRowFilters(new Set());
  };

  const updateFilteredFiles = () => {
    const totalFiles = row?.files;
    let newFilteredFileSize = 0;
    const newFilteredFiles = totalFiles.filter((file) => {
      let displayLocation;
      if (row?.kingdom === "phytozome")
        displayLocation = file?.metadata?.type.split("/");
      else displayLocation = file?.metadata?.portal?.display_location;
      return [...rowFilters].some((filter) => {
        const filterPath = filter.split("/");
        const includeFile = filterPath.every((path, idx) => {
          return displayLocation?.[idx] === path;
        });
        if (includeFile) newFilteredFileSize += file.file_size;
        return includeFile;
      });
    });
    setFilteredFiles(newFilteredFiles);
    setFilteredFileSize(newFilteredFileSize);
  };

  useEffect(() => {
    if (rowFilters.size === 0) {
      setFilteredFiles(row?.files);
      setFilteredFileSize(row?.fileSize);
    } else {
      updateFilteredFiles();
    }
  }, [rowFilters, row]);

  useEffect(() => {
    if (row || selectedFiles) {
      const selIds = selectedForRow(selectedFiles, row.id);
      const cartIds = cartTool.fileIdsForOrgId(cartData, row.id);

      let state = ROW_SELECTED_NONE;

      if ((!restoreid || cartpage) && cartIds.length === filteredFiles.length) {
        state = ROW_ALL_IN_CART;
      } else if (selIds.length > 0) {
        if (
          selIds.length + (restoreid ? 0 : cartIds.length) ===
          filteredFiles.length
        ) {
          state = ROW_SELECTED_ALL;
        } else {
          state = ROW_SELECTED_SOME;
        }
      }

      if (state !== selectState) {
        setSelectState(state);
      }
    }
  }, [row, selectedFiles, filteredFiles]);

  const [isHovering, setHovering] = useState(false);

  const enter = () => {
    setHovering(true);
  };

  const leave = () => {
    setHovering(false);
  };

  const handleClickDirectory = (e, fullPath) => {
    e.stopPropagation(); // if we click on just the text, we don't want to expand
    const doNotAskConfirm = getLocalStorageHideSelectionOK();
    if (selectState === ROW_SELECTED_SOME || selectState === ROW_SELECTED_ALL) {
      if (
        !doNotAskConfirm &&
        ((rowFilters.has(fullPath) && rowFilters.size !== 1) ||
          rowFilters.size === 0)
      ) {
        // we are removing a filter OR adding the first filter
        setVirtualDirectoryStructureAnchor(null);
        setAcceptConfirmationHandler(() => () => {
          updateRowFilters(fullPath);
        });
        setConfirmApplyType(CONFIRM_APPLY_LOCAL_FILTER);
        return;
      }
    }
    updateRowFilters(fullPath);
    const honeycombPayload = {
      node: fullPath,
      genome_id: row.id,
      // anything else?
    };
    honeycomb.sendUiInteractionSpan("clicked-vds-node", honeycombPayload);
  };

  return (
    <>
      <TableRow
        key={row.id}
        data-testid="GenomeTableRow"
        id={`GenomeTableRow-${row.id}`}
        className={expanded ? classes.rowExpand : classes.row}
        onMouseEnter={enter}
        onMouseLeave={leave}
      >
        <GenomeTableCells
          row={row}
          onRowClick={onRowClick}
          expanded={expanded}
          isHovering={isHovering}
          selectState={selectState}
          ldClient={ldClient}
          cartData={cartData}
          restoreid={restoreid}
          {...props}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          rowFilters={rowFilters}
          resetRowFilters={resetRowFilters}
          filteredFiles={filteredFiles}
          filteredFileSize={filteredFileSize}
          virtualDirectoryStructureAnchor={virtualDirectoryStructureAnchor}
          setVirtualDirectoryStructureAnchor={
            setVirtualDirectoryStructureAnchor
          }
          handleClickDirectory={handleClickDirectory}
          honeycomb={honeycomb}
        />
      </TableRow>
      <TableRow key={`${row.id}-files-table`}>
        <TableCell padding="none" colSpan={7}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {/* the file table under the genome row, when expanded */}
            <FileTable
              {...props}
              fileGrouping={row}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              onSelectAllClicked={() =>
                handleGenomeLevelCheckboxClicked(row, filteredFiles)
              }
              selectState={selectState}
              rowId={row.id}
              ldClient={ldClient}
              cartData={cartData}
              restoreid={restoreid}
              files={filteredFiles}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default GenomeRow;

GenomeRow.defaultProps = {
  selectedFiles: null,
  row: {},
  onRowClick: () => {},
  handleGenomeLevelCheckboxClicked: () => {},
  expanded: false,
  honeycomb: {
    sendUiInteractionSpan: () => {},
  },
  props: null,
  ldClient: null,
  cartData: null,
  restoreid: null,
  setAcceptConfirmationHandler: () => {},
  setConfirmApplyType: () => {},
};

GenomeRow.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    files: PropTypes.arrayOf(PropTypes.shape()),
    fileSize: PropTypes.number,
    kingdom: PropTypes.string,
  }),
  expanded: PropTypes.bool,
  onRowClick: PropTypes.func,
  handleGenomeLevelCheckboxClicked: PropTypes.func,
  honeycomb: PropTypes.shape({
    sendUiInteractionSpan: PropTypes.func,
  }),
  props: PropTypes.shape(),
  ldClient: PropTypes.shape(),
  setSelectedFiles: PropTypes.func.isRequired,
  selectedFiles: PropTypes.arrayOf(PropTypes.shape()),
  cartData: PropTypes.shape(),
  restoreid: PropTypes.string,
  setAcceptConfirmationHandler: PropTypes.func,
  setConfirmApplyType: PropTypes.func,
};
